import { DOCUMENT, inject, Injectable, Renderer2 } from '@angular/core';
import { delay } from '@maplecroft-typescript/utils';
import { downloadBlob } from '@maplecroft-ui/ng-utils';
import { domToBlob } from 'modern-screenshot';
import { ScorePanelAsImageRequiredData } from '../model';

@Injectable({
  providedIn: 'root',
})
export class DownloadScorePanelAsImageService {
  private readonly document = inject(DOCUMENT);

  // Return a canvas element based on a given svg, this is done so that the
  public async convertHtmlToImageAndDownload(renderer: Renderer2, data: ScorePanelAsImageRequiredData): Promise<void> {
    // Method to convert the html element to a png and download it to the user

    // If this component has been setup incorrectly, you can't download the
    // image
    if (data.linkedElement) {
      // Clone the reference and pretend it's an html element to help with
      // styling
      const cloneLink = data.linkedElement.cloneNode(true);

      // Do this only for scoreCard.
      if (data.linkedElement.classList.contains('grid-ui-image-download-container')) {
        renderer.setStyle(cloneLink.childNodes[0].childNodes[3], 'height', 'auto');
      }

      // Do this only for scoreMap.
      if (data.linkedElement.classList.contains('score-map-selector')) {
        renderer.setStyle(cloneLink.childNodes[1], 'display', 'none');
      }

      // Fetch the old width to make sure it remains the same + include the
      // extra padding involved
      if (!data.optionalStyle) {
        data.optionalStyle = {};
      }

      data.optionalStyle.width = data.linkedElement.clientWidth + 100 + 'px';

      // Add the cloneRef to the hidden offscreen element, this is required
      // for htmlcanvas to find it
      data.cloneRef.nativeElement.appendChild(cloneLink);

      // Add title and logo
      // append header title
      if (data.headerRef) {
        renderer.setStyle(cloneLink, 'padding-top', '50px');

        const header = data.headerRef.nativeElement.cloneNode(true) as HTMLElement;
        // remove buttons from header
        const buttonsWrapper = header.getElementsByClassName('header-buttons-wrapper');
        if (buttonsWrapper && buttonsWrapper.length > 0) {
          header.removeChild(buttonsWrapper[0]);
        }
        renderer.setStyle(header, 'width', '100%');
        renderer.setStyle(header, 'margin-bottom', '0');
        renderer.setStyle(header, 'left', '0');
        renderer.setStyle(header, 'top', '0');

        cloneLink.insertBefore(header, cloneLink.firstChild);
      } else {
        cloneLink.insertBefore(this.createTitle(data), cloneLink.firstChild);
      }

      if (data.addScoreLegendToImage) {
        cloneLink.appendChild(this.createLegend(data));
      }

      cloneLink.appendChild(this.createLogo());

      // Hide elements that should not be visible in the image
      if (data.hideElementSelectors?.length) {
        data.hideElementSelectors.forEach((selector) =>
          document.querySelectorAll(selector).forEach((element) => {
            const htmlEl = element as HTMLElement;
            htmlEl.dataset.originalDisplay = htmlEl.style.display;
            htmlEl.style.display = 'none';
          }),
        );
      }

      // Wait for the styles to take hold
      await delay(100);

      // eslint-disable-next-line no-async-promise-executor
      return new Promise(async (resolve, reject) => {
        try {
          const blob = await domToBlob(data.cloneRef.nativeElement, { scale: 2 });

          const fileName = data.subtitle ? `${data.subtitle}` : data.title;

          await downloadBlob(blob, `${fileName}.png`, this.document);

          data.cloneRef.nativeElement.removeChild(cloneLink);

          resolve();
        } catch (error) {
          console.error(error);

          reject('The error occurred while converting this image.');
        }

        if (data.hideElementSelectors?.length) {
          data.hideElementSelectors.forEach((selector) => {
            document.querySelectorAll(selector).forEach((el) => {
              const htmlEl = el as HTMLElement;
              htmlEl.style.display = htmlEl.dataset.originalDisplay ?? '';
              delete htmlEl.dataset.originalDisplay;
            });
          });
        }
      });
    }
  }

  // Function to create a simple logo div
  private createLogo(): HTMLDivElement {
    const bigdiv = document.createElement('div');
    bigdiv.style.display = 'flex';
    bigdiv.style.width = '100%';
    bigdiv.style.height = 'auto';
    bigdiv.style.marginTop = '30px';
    bigdiv.style.justifyContent = 'space-between';
    bigdiv.style.alignItems = 'center';

    const img = document.createElement('img');
    img.src = 'assets/img/vm-logo-tm.svg';
    img.height = 30;
    bigdiv.appendChild(img);

    const copyrightTextElement = document.createElement('span');
    copyrightTextElement.style.color = '#54584a';
    const currentYear = new Date().getFullYear();
    copyrightTextElement.textContent = `© Verisk Maplecroft ${currentYear}`;
    bigdiv.appendChild(copyrightTextElement);

    return bigdiv;
  }

  // Function to create the div element
  private createTitle(data: ScorePanelAsImageRequiredData): HTMLDivElement {
    const bigdiv = document.createElement('div');
    bigdiv.style.marginBottom = '41.5px';

    const title = document.createElement('div');

    // nosemgrep: javascript.browser.security.insecure-document-method.insecure-document-method
    title.innerHTML = data.title;
    title.style.color = '#002d61';
    title.style.fontSize = '29px';
    title.style.height = '29px';
    bigdiv.appendChild(title);

    if (data.subtitle) {
      const subtitle = document.createElement('div');

      // nosemgrep: javascript.browser.security.insecure-document-method.insecure-document-method
      subtitle.innerHTML = data.subtitle;
      subtitle.style.color = '#262626';
      subtitle.style.fontSize = '24px';
      subtitle.style.height = '24px';
      subtitle.style.marginTop = '6px';
      subtitle.style.lineHeight = 'normal';
      bigdiv.appendChild(subtitle);
    }
    return bigdiv;
  }

  private createLegend(data: ScorePanelAsImageRequiredData): HTMLElement {
    const legendNode = data.legendRef.nativeElement.cloneNode(true) as HTMLElement;
    return legendNode;
  }
}
